import { format, isValid } from "date-fns";
import { DataGridSortableColumns, PublishedSourceBundlesQuery, SortDirection } from "../../graphql/types";
import { OptionalRange } from "../../types";
import { ImportFilter, SourceNode, toProjectImportNodes } from "../common/FilterPublishedArtifacts";
import { DateRange } from "../Date";
import { addChildToNode } from "../Filters/Common";

const getDateRange = (start?: string, finish?: string) => {
  if (!start || !finish) return undefined;
  return DateRange.fromString(start, finish)?.toDateRange();
};

interface IDownloadFilters {
  importNames: string[];
  bundleNames: string[];
  search: string;
  status: string;
  reportRange: OptionalRange<Date>;
  projects: string[];
  purpose: string;
  downloadedRange: OptionalRange<Date>;
  sortBy?: DataGridSortableColumns;
  sortDirection?: SortDirection;
}

const getDownloadsDatagridQueryVariables = (filter: IDownloadFilters) => {
  const today = format(new Date(), "yyyy-MM-dd");

  let reportDatetimeEnd: string | null = null;
  let reportDatetimeStart: string | null = null;

  if (
    filter.reportRange.end &&
    isValid(filter.reportRange.end) &&
    filter.reportRange.start &&
    isValid(filter.reportRange.start)
  ) {
    const endDateString = format(filter.reportRange.end, "yyyy-MM-dd");
    if (endDateString !== today) {
      reportDatetimeEnd = endDateString;
    }
    reportDatetimeStart = format(filter.reportRange.start, "yyyy-MM-dd");
  }
  let createdDatetimeBefore: string | null = null;
  let createdDatetimeAfter: string | null = null;
  if (
    filter.downloadedRange.end &&
    isValid(filter.downloadedRange.end) &&
    filter.downloadedRange.start &&
    isValid(filter.downloadedRange.start)
  ) {
    const endDateString = format(filter.downloadedRange.end, "yyyy-MM-dd");
    if (endDateString !== today) {
      createdDatetimeBefore = endDateString;
    }
    createdDatetimeAfter = format(filter.downloadedRange.start, "yyyy-MM-dd");
  }

  return {
    first: 20,
    project: null,
    name: null,
    bundle_name: null,
    projects: filter.projects?.length ? filter.projects : null,
    names: filter.importNames?.length ? filter.importNames : null,
    bundleNames: filter.bundleNames?.length ? filter.bundleNames : null,
    purpose: filter.purpose,
    search: filter.search,
    status: ["newDirtyGreaterThanDirty", "dirtyEqualsNewDirty"].includes(filter.status) ? null : filter.status,
    report_datetime_before: reportDatetimeEnd,
    report_datetime_after: reportDatetimeStart,
    downloaded_before: createdDatetimeBefore,
    downloaded_after: createdDatetimeAfter,
    newDirtyGreaterThanDirty: filter.status === "newDirtyGreaterThanDirty" ? true : null,
    dirtyEqualsNewDirty: filter.status === "dirtyEqualsNewDirty" ? true : null,
    sortBy: filter.sortBy,
    sortDirection: filter.sortDirection,
  };
};

type DownloadFiltersParamNames = {
  [K in keyof Omit<IDownloadFilters, "downloadedRange" | "reportRange">]-?: string;
} & {
  reportRange: { start: string; end: string };
  downloadedRange: { start: string; end: string };
};

const getDefaultDownloadFilters = (
  searchParams: URLSearchParams,
  useDefaultDateRange?: boolean,
  variables?: Partial<IDownloadFilters>,
  paramNames?: DownloadFiltersParamNames
): IDownloadFilters => {
  return {
    projects: variables?.projects || searchParams.get(paramNames?.projects || "projects")?.split(",") || [],
    importNames:
      variables?.importNames || searchParams.get(paramNames?.importNames || "downloadNames")?.split(",") || [],
    bundleNames: variables?.bundleNames || searchParams.get(paramNames?.bundleNames || "bundleNames")?.split(",") || [],
    purpose: variables?.purpose || searchParams.get(paramNames?.purpose || "purpose") || "",
    search: variables?.search || searchParams.get(paramNames?.search || "search") || "",
    status: variables?.status || searchParams.get(paramNames?.status || "status") || "",
    reportRange: getDateRange(
      (variables?.reportRange?.start && format(variables?.reportRange?.start, "yyyy-MM-dd")) ||
        searchParams.get(paramNames?.reportRange?.start || "start") ||
        undefined,
      (variables?.reportRange?.end && format(variables?.reportRange?.end, "yyyy-MM-dd")) ||
        searchParams.get(paramNames?.reportRange?.end || "end") ||
        undefined
    ) || {
      start: null,
      end: null,
    },
    downloadedRange:
      getDateRange(
        (variables?.downloadedRange?.start && format(variables?.downloadedRange?.start, "yyyy-MM-dd")) ||
          searchParams.get(paramNames?.downloadedRange?.start || "downloaded_start") ||
          undefined,
        (variables?.downloadedRange?.end && format(variables?.downloadedRange?.end, "yyyy-MM-dd")) ||
          searchParams.get(paramNames?.downloadedRange?.end || "downloaded_end") ||
          undefined
      ) ||
      (useDefaultDateRange
        ? DateRange.fromDays(7).toDateRange()
        : {
            start: null,
            end: null,
          }),
    sortBy: (searchParams.get(paramNames?.sortBy || "sortBy") as DataGridSortableColumns) || undefined,
    sortDirection: (searchParams.get(paramNames?.sortDirection || "sortDirection") as SortDirection) || undefined,
  };
};

const matchesFilter = (filterValue: ImportFilter, filter: IDownloadFilters) =>
  (filterValue.project && filter.projects?.includes(filterValue.project)) ||
  (filterValue.importName && filter.importNames?.includes(filterValue.importName));

const getDownloadsNodes = (data: PublishedSourceBundlesQuery | undefined, filter: IDownloadFilters) => {
  let nodes: SourceNode[] = [];
  const nonSelectedNodes: SourceNode[] = [];
  const selectedNodes: SourceNode[] = [];

  if (data) {
    const bundles = data.source_bundles?.edges.map((edge) => edge.node) ?? [];
    nodes = toProjectImportNodes(bundles, true, true);
  }

  nodes.forEach((node: SourceNode) => {
    const filterValue = node.filterValue as ImportFilter;
    const isMatching = matchesFilter(filterValue, filter);

    if (node.children.length === 0) {
      // If the node has no children, we can add it directly to the selected or non-selected nodes
      // Otherwise it will be added to the selected or non-selected nodes when we process its children
      (isMatching ? selectedNodes : nonSelectedNodes).push(node);
    }

    node.children.forEach((child) => {
      const childFilterValue = child.filterValue as ImportFilter;
      if (matchesFilter(childFilterValue, filter)) {
        addChildToNode(selectedNodes, child, node);
      } else {
        addChildToNode(nonSelectedNodes, child, node);
      }
    });
  });
  return { selectedNodes, nonSelectedNodes };
};

export {
  DownloadFiltersParamNames,
  getDefaultDownloadFilters,
  getDownloadsDatagridQueryVariables,
  getDownloadsNodes,
  IDownloadFilters,
};
